// The `evidence/guidance` zone-setup WRITE — carved from
// `src/hooks/mutation/useMutateSetupHealthPathways.ts` (now a bodied delegate).
//
// THE INVALIDATION IS A PORT BECAUSE THE CACHE IS NOT THIS FAMILY'S. `onSuccess`
// evicted `[GET_EVIDENCE_INTEGRATIONS]` — the INTEGRATIONS lane's identity,
// declared in that lane's own hook — and this package never names it. That is
// the delivery command rule applied to a cache eviction, and it is the same
// arrangement the sibling family reached for workspace's `invalidateCurrentUser`:
// a foreign cache eviction fired after a write is an app reaction, not a
// cross-lane wait, and the difference is that a wait is a data dependency the
// package needs in order to work at all.
//
// It is a PORT rather than a `configureEvidenceData` input for the same reason
// the sibling gave: injecting another domain's invalidation into the seam would
// hide an edge the manifest cannot see. The seam carries the app's `QueryClient`
// for this package's own module-scope cache commands and nothing else.
//
// T2 RATHER THAN THE `meta` TIER, and the reason is stronger here than for the
// sibling writes. The delegate is bodied WHATEVER the copy does — it has to fire
// the foreign invalidation — so a `meta` toast could not dissolve it. On the
// copy's own merits it also fails the T1 bar twice: the error message is chosen
// by HTTP STATUS across four branches (400 / 502 / 503 / everything else) with
// the backend's `detail.msg` substituted ahead of all of them, and one failure
// renders NO toast at all.
//
// THAT SUPPRESSION STAYS AT THE DELEGATE, which is the one judgement call in
// this file. `zone_not_supported` is the postcode-has-no-pathway-set case, and
// the setup dialog renders it inline instead of toasting. The package could
// withhold the port for it — the predicate lives here — but "which failures the
// user is told about, and how" is precisely what §7a keeps app-side, so the port
// fires for EVERY rejection with the error unchanged and the delegate decides.
// The predicate is exported (and re-exported by the delegate) because the dialog
// classifies the same rejection to render it.
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import {
  HEALTHPATHWAYS_ZONE_NOT_SUPPORTED_CODE,
  SETUP_HEALTHPATHWAYS,
  type HealthPathwaysSetupRequest,
} from '../../contracts';
import { setupHealthPathwaysZone } from '../api/healthpathways';

export function isHealthPathwaysZoneNotSupportedError(error: unknown): boolean {
  return (
    error instanceof AxiosError &&
    error.response?.status === 400 &&
    (error.response?.data as { detail?: { code?: string } })?.detail?.code ===
      HEALTHPATHWAYS_ZONE_NOT_SUPPORTED_CODE
  );
}

export interface UseMutateSetupHealthPathwaysOptions {
  /**
   * App-reaction port: fired where `invalidateQueries([GET_EVIDENCE_INTEGRATIONS])`
   * fired, and AWAITED before the success port — the pre-move order, so the
   * settings card and the dialog re-read a refreshed integrations list rather
   * than the one that predates the zone.
   */
  onIntegrationsStale?: () => Promise<void> | void;
  /** App-reaction port: fired where the success toast fired. */
  onSetupComplete?: () => void;
  /**
   * App-reaction port: fired where the error toast fired, with the error
   * unchanged — the delegate reads `response.status` and `response.data.detail.msg`
   * off it, and suppresses the toast entirely for the inline-handled case.
   */
  onSetupError?: (error: Error) => void;
}

export function useMutateSetupHealthPathways({
  onIntegrationsStale,
  onSetupComplete,
  onSetupError,
}: UseMutateSetupHealthPathwaysOptions = {}) {
  return useMutation({
    mutationKey: [SETUP_HEALTHPATHWAYS],
    mutationFn: async (payload: HealthPathwaysSetupRequest) => {
      const { data } = await setupHealthPathwaysZone(payload);
      return data;
    },
    onSuccess: async () => {
      await onIntegrationsStale?.();

      onSetupComplete?.();
    },
    onError: (error: Error) => {
      onSetupError?.(error);
    },
  });
}
